import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  ExternalLink,
  Info,
  Ticket,
  Train,
  Car,
  DoorOpen,
  BookOpen,
} from "lucide-react";

const FinalEventPage = () => {
  const { t, language } = useLanguage();

  const formatDate = (date: string) => {
    if (language === "en") {
      return "May 10, 2025";
    }
    return date;
  };

  const formatTime = (time: string) => {
    if (language === "en") {
      const [start, end] = time.split(" - ");
      const formatHour = (hour: string) => {
        const [h, m] = hour.split(":");
        return `${h}:${m} ${Number(h) >= 12 ? "PM" : "AM"}`;
      };
      return `${formatHour(start)} - ${formatHour(end)}`;
    }
    return time;
  };

  return (
    <div className="pt-16">
      <div className="bg-primary text-primary-foreground">
        <div className="container mx-auto py-16 px-4 md:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t("final.title")}
          </h1>
          <p className="text-xl opacity-90">{t("final.subtitle")}</p>
        </div>
      </div>

      <div className="container mx-auto py-12 px-4 md:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4">{t("final.title")}</h2>
              <p className="mb-6">{t("final.description")}</p>

              <div className="flex flex-col space-y-4 mb-6">
                <div className="flex items-center">
                  <Calendar className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{t("events.date")}</strong>{" "}
                    {formatDate("10 maja 2025")}
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{t("events.time")}</strong>{" "}
                    {formatTime("17:00 - 19:00")}
                  </span>
                </div>
                <div className="flex items-center">
                  <MapPin className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{t("events.location")}</strong>{" "}
                    {t("final.subtitle")}, {t("final.hall")} B17
                  </span>
                </div>
                <div className="flex items-center">
                  <Users className="mr-3 h-5 w-5 text-primary" />
                  <span>
                    <strong>{t("events.entry")}</strong>{" "}
                    {t("events.entry.price")}
                  </span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button
                  asChild
                  variant="destructive"
                  className="w-full sm:w-auto"
                >
                  <a
                    href="https://app.evenea.pl/event/kaznodziejazkarabinem-poznan/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center"
                  >
                    {t("events.buyTicket")}{" "}
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <Button
                  asChild
                  variant="secondary"
                  className="w-full sm:w-auto"
                >
                  <a
                    href="https://www.facebook.com/events/1029506549032059/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center"
                  >
                    {t("events.facebook.join")}{" "}
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-4">{t("events.agenda")}</h2>
              <div className="space-y-4">
                <div className="p-4 border-l-4 border-primary">
                  <div className="text-sm text-muted-foreground">
                    17:00–17:05
                  </div>
                  <div className="font-semibold">
                    {t("events.agenda.opening")}
                  </div>
                </div>
                <div className="p-4 border-l-4 border-primary">
                  <div className="text-sm text-muted-foreground">
                    17:05–17:40
                  </div>
                  <div className="font-semibold">
                    {t("events.agenda.worship")}
                  </div>
                </div>
                <div className="p-4 border-l-4 border-primary">
                  <div className="text-sm text-muted-foreground">
                    17:40–17:50
                  </div>
                  <div className="font-semibold">
                    {t("events.agenda.announcements")}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t("events.agenda.announcements.description")}
                  </div>
                </div>
                <div className="p-4 border-l-4 border-primary">
                  <div className="text-sm text-muted-foreground">
                    17:50–18:40
                  </div>
                  <div className="font-semibold">
                    {t("events.agenda.speech")}
                  </div>
                </div>
                <div className="p-4 border-l-4 border-primary">
                  <div className="text-sm text-muted-foreground">
                    18:40–19:00
                  </div>
                  <div className="font-semibold">
                    {t("events.agenda.closing")}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-sm text-muted-foreground">
                {t("events.agenda.note")}
              </div>
            </div>
          </div>

          <div>
            <div className="sticky top-24 space-y-8">
              <div className="rounded-lg overflow-hidden">
                <img
                  src="/lovable-uploads/df481845-4045-41c1-9dd3-80b32c49e6f6.png"
                  alt="Sam Childers speaking"
                  className="w-full h-auto"
                />
              </div>

              <div className="bg-secondary p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">
                  {t("final.practical.title")}
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <Ticket className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span>
                      {language === "en"
                        ? "Entry fee is 20 PLN. Tickets will be available at the entrance or can be purchased in advance through registration."
                        : "Wstęp 20 zł. Bilety dostępne przy wejściu lub do nabycia wcześniej poprzez rejestrację."}
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Train className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span>{t("final.practical.transport")}</span>
                  </li>
                  <li className="flex items-start">
                    <Car className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span>{t("final.practical.parking")}</span>
                  </li>
                  <li className="flex items-start">
                    <DoorOpen className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span>{t("final.practical.entrance")}</span>
                  </li>
                  <li className="flex items-start">
                    <BookOpen className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span>{t("final.practical.books")}</span>
                  </li>
                </ul>
              </div>

              <Button
                className="w-full"
                size="lg"
                variant="destructive"
                asChild
              >
                <a
                  href="https://app.evenea.pl/event/kaznodziejazkarabinem-poznan/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center"
                >
                  {language === "en" ? "Buy Ticket" : "Kup Bilet"}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <div className="bg-card border p-6 rounded-lg">
            <div className="max-w-5xl mx-auto">
              <h2 className="flex items-center text-2xl font-bold mb-6">
                <Info className="mr-2 h-6 w-6 text-primary" />
                {t("final.stadium.title")}
              </h2>
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-lg mb-4">{t("stadium.description")}</p>
                  <p className="text-muted-foreground">
                    {t("stadium.room.description")}
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="/lovable-uploads/e472e976-ad07-4ccb-b873-27b6ae17b0b2.png"
                    alt={t("final.stadium.image.alt")}
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalEventPage;
